import React, {
  Children,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { useAuthContext } from "../components/Auth";
import { CaretDownIcon, HamburgerIcon, XIcon } from "../components/Icons/Icons";
import { useRoutePath } from "./Routing";
import { useMediaQueries, useStoreState } from "./util";

import type { ReactElement, ReactNode } from "react";
import styled, { ThemeContext } from "styled-components/macro";
import {
  InvisibleButton,
  PrimaryButtonFitContainer,
} from "../components/Buttons/Buttons";
import { NavItemWrapper } from "../layout/shared";
import { screenSize } from "../theme";
import type { CustomPageConfig } from "../types/types";
import { useOnClickOutside } from "./hooks";
import { spacingTokens } from "../designTokens";

interface IHamburgerWrapperProps {
  isOpen: boolean;
}

interface ITopNavProps {
  isMediumScreen: boolean;
  isMenuVisible: boolean;
}

interface IDropDownMenu {
  menuX?: number;
  menuY?: number;
}

interface NavItem extends Partial<CustomPageConfig> {
  nav_link_text: string | null;
  path: string;
  priority?: number;
}

const TopNavWrapper = styled.div<ITopNavProps>`
  display: ${({ isMediumScreen }) => (isMediumScreen ? "none" : "flex")};
  overflow: hidden;
  width: 100%;
  min-width: 40px;
  margin: 3px 10px 3px;
  position: relative;
  justify-content: ${({ isMenuVisible }) =>
    isMenuVisible ? "left" : "center"};
  opacity: ${({ isMediumScreen }) => (isMediumScreen ? 0 : 1)};
  & > div > div {
    height: 100%;
  }
`;

const HamburgerWrapper = styled.div<IHamburgerWrapperProps>`
  order: 99;
  left: 100%;
  background-color: white;
  position: sticky;
  padding: ${({ theme, isOpen }) =>
    isOpen
      ? `${theme.resolve("spacing_xs")} 0 ${theme.resolve("spacing_xxs")}`
      : `${theme.resolve("spacing_sm")} ${theme.resolve(
          "spacing_xxs"
        )} ${theme.resolve("spacing_xxs")}`};
  border: ${({ isOpen, theme }) =>
    isOpen
      ? `${theme.resolve("border_md")} solid ${theme.primaryBorder}`
      : `${theme.resolve("border_md")} solid transparent`};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  margin-right: 15px;
  align-self: center;
  @media ${screenSize.medium} {
    margin-right: 5px;
  }
`;

const GuestUserWrapper = styled.div`
  display: flex;
  padding: 15px 0 10px;
  flex-direction: column;
  justify-content: space-evenly;
`;
const DropDown = styled.div<IDropDownMenu>`
  position: absolute;
  top: ${({ menuY }) => (menuY ? `${menuY}px` : "58px")};
  right: ${({ menuX }) => (menuX ? `${menuX}px` : "255px")};
  min-width: 180px;
  padding: 5px 15px;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  background: #fff;
  box-shadow: 0px 6px 16px -6px rgba(0, 0, 0, 0.44);
  border-radius: 5px;
  z-index: 99;
  @media (max-width: 580px) {
    right: 0;
    left: 0;
  }
  button {
    display: block;
    width: 100%;
    background: #fff;
    border-radius: 0;
    border: ${({ theme }) => theme.resolve("border_md")} solid #fff;
    cursor: pointer;
    padding: 10px 0;
    text-align: left;
    &:hover {
      background: #f5f7f8;
    }
  }
  a {
    padding: ${({ theme }) => theme.resolve("spacing_md")} 0
      ${({ theme }) => theme.resolve("spacing_sm")};
    border-bottom: ${({ theme }) => theme.resolve("border_3xl")} solid
      transparent;
  }
  a:hover {
    background: ${({ theme }) => `${theme.primaryBG}`};
  }
  a.active {
    border-right: 0;
    border-bottom: ${({ theme }) =>
      `${theme.resolve("border_3xl")} solid ${theme.brandColor}`};
    background: ${({ theme }) => `${theme.primaryBG}`};
  }
`;

const LeftDropDown = styled(DropDown)`
  right: unset;
  left: ${({ menuX }) => (menuX ? `${menuX}px` : "255px")};
`;

const DataDropdownLink = styled(Link)`
  display: block;
  width: 100%;
  padding: 10px 0;
  text-decoration: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: inherit;
  &:hover {
    color: ${({ theme }) => theme.brandColor};
  }
  &.active {
    border-bottom: ${({ theme }) => theme.resolve("border_3xl")} solid
      ${({ theme }) => theme.brandColor};
  }
`;

const LoginButton = styled(Link)`
  background: ${({ theme }) => theme.secondaryBG};
  text-align: center;
  padding: 10px ${({ theme }) => theme.resolve("spacing_2xl")};
  border-radius: 3px;
  margin: 10px 0 0;
  text-decoration: none;
  text-wrap: nowrap;
  font-size: ${(props) => props.theme.fontSizes.medium};
  color: ${(props) => props.theme.resolve("color_text_primary")};
  @media ${screenSize.small} {
    padding: 10px;
  }
`;

const RegisterButton = styled(PrimaryButtonFitContainer)`
  text-align: center;
  border: ${({ theme }) => theme.resolve("border_2xl")} solid
    ${(props) => props.theme.selectedBorder} !important;
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  font-weight: ${(props) => props.theme.fontWeights.medium};
  padding: 10px ${({ theme }) => theme.resolve("spacing_2xl")};
  box-shadow: 0px 0px 8px #00000040;
  text-decoration: none;
  @media ${screenSize.small} {
    padding: 5px;
  }
`;

const DropdownContainer = styled.div.attrs({ className: "dropdown-container" })`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button<{ isExpanded?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.resolve("spacing_sm")}
    ${({ theme }) => theme.resolve("spacing_2xl")};
  width: 100%;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  border-bottom: ${({ theme }) => theme.resolve("border_2xl")} solid
    ${({ theme, isExpanded }) =>
      isExpanded ? theme.publicNavBorderActive : "transparent"};
  text-align: left;

  &:hover {
    color: ${({ theme }) => theme.brandColor};
  }
`;

const ActiveDropdownButton = styled(DropdownButton)`
  &.active {
    color: ${({ theme }) => theme.brandColor};
  }
`;

const CaretIconWrapper = styled.span<{ isExpanded: boolean }>`
  display: inline-flex;
  transform: rotate(${({ isExpanded }) => (isExpanded ? "180deg" : "0deg")});
  transition: transform 0.4s ease;
`;

const DATA_DROPDOWN_LINK_TOP = 32.5; // ideal distance from top of button to top of dropdown

export function IntersectionObserverWrapper({
  children,
}: {
  children: ReactNode;
}) {
  const navRef = useRef<HTMLDivElement>(null);
  const { isMediumScreen } = useMediaQueries();
  const {
    storefront_metadata: { custom_pages, route_configuration },
    slug,
  } = useStoreState();
  const [visibilityMap, setVisibilityMap] = useState<{
    [key: string]: boolean;
  }>({});
  const { t, i18n } = useTranslation();

  const orderedChildren = () => {
    const childrenArray = Children.toArray(children);

    if (!custom_pages?.length && !route_configuration?.length) {
      return childrenArray;
    }

    // Determine whether a custom "home" page exists.
    // We compare in lower case to avoid case-sensitivity issues.
    const customHomeExists = custom_pages?.some(
      (page) =>
        page.nav_link_text &&
        t(page.nav_link_text).toLowerCase() === t("home").toLowerCase()
    );

    const navItems: NavItem[] = [
      // Add route_configuration items, excluding:
      // 1. Items that are not enabled,
      // 2. The default "home" route if a custom home exists,
      // 3. Items for which a custom page already exists (by comparing the path).
      ...(route_configuration
        ?.filter(
          (route) =>
            route.enabled &&
            !(route.route === "home" && customHomeExists) &&
            !custom_pages?.some((page) => page.path === `/${route.route}`)
        )
        .map((route) => ({
          nav_link_text: t(route.translations["en"]),
          path: `/${route.route}`,
          priority: 0,
        })) ?? []),

      // Add custom_pages items with translations
      ...(custom_pages?.map((page) => {
        return {
          ...page,
          nav_link_text:
            !page.is_system &&
            page.page_content.find(
              (content) => content.language === i18n.language
            )?.nav_link_text
              ? (page.page_content.find(
                  (content) => content.language === i18n.language
                )?.nav_link_text as string)
              : route_configuration.find(
                  (route) => page.path === `/${route.route}` && route.enabled
                )?.translations[i18n.language] &&
                route_configuration.find(
                  (route) => page.path === `/${route.route}` && route.enabled
                )?.translations[i18n.language] !== undefined
              ? (route_configuration.find(
                  (route) => page.path === `/${route.route}`
                )?.translations[i18n.language] as string)
              : page.nav_link_text !== null && t(page.nav_link_text)
              ? t(page.nav_link_text)
              : page.nav_link_text ?? page.path.substring(1),
        };
      }) ?? []),
    ];

    // First identify all parent-child relationships
    const parentChildMap: Record<string, CustomPageConfig[]> = {};
    navItems.forEach((page) => {
      if ("parent_path" in page && page.parent_path) {
        // Initialize array if this is the first child for this parent

        if (!parentChildMap[page.parent_path]) {
          parentChildMap[page.parent_path] = [];
        }
        parentChildMap[page.parent_path].push(page as CustomPageConfig);
      }
    });

    // Create final ordered array
    return navItems
      .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
      .filter((page) => {
        // Keep items that are either:
        // 1. Regular nav items (no parent_path)
        // 2. Parent items that have children
        const result =
          !("parent_path" in page) &&
          page.nav_link_text &&
          (!("is_parent" in page) ||
            (page.is_parent && parentChildMap[page.path ?? ""]?.length > 0));

        return result;
      })
      .map((page) => {
        const navText = page.nav_link_text;
        if (!navText) return null;

        // If this is a parent with children, render as dropdown
        if (
          "is_parent" in page &&
          page.is_parent &&
          page.path &&
          parentChildMap[page.path]?.length > 0
        ) {
          return React.cloneElement(
            <TopNavDropdown
              parentName={navText}
              childPages={parentChildMap[page.path]}
              tenantSlug={slug!}
            />,
            {
              name: navText,
              path: page.path,
            }
          );
        }

        // For regular nav items, find matching child and render with consistent visibility check
        const matchingChild = childrenArray.find(
          (item) => (item as ReactElement)?.props.name === navText
        );

        if (matchingChild) {
          return React.cloneElement(matchingChild as ReactElement, {
            "data-targetid": navText,
            isVisible: visibilityMap[navText] !== false,
          });
        }

        return null;
      })
      .filter(Boolean);
  };

  const handleIntersection = useCallback((entries: any[]) => {
    const updatedEntries: { [key: string]: boolean } = {};
    entries.forEach((entry) => {
      const targetid: string | undefined = entry?.target.dataset.targetid;
      // Check if element is visibile within container
      if (targetid) {
        if (entry.isIntersecting) {
          updatedEntries[targetid] = true;
        } else {
          updatedEntries[targetid] = false;
        }
      }
    });

    setVisibilityMap((prev) => ({
      ...prev,
      ...updatedEntries,
    }));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: navRef.current,
      threshold: 0.85,
    });

    if (navRef.current) {
      Array.from(navRef.current.children).forEach((item) => {
        observer.observe(item);
      });
      return () => {
        setVisibilityMap({});
        observer.disconnect();
      };
    }
  }, [handleIntersection, children]);

  return (
    <>
      <TopNavWrapper
        ref={navRef}
        isMediumScreen={isMediumScreen}
        isMenuVisible={Object.values(visibilityMap).some((v) => v === false)}
      >
        {React.Children.map(orderedChildren(), (child) => {
          return (
            <NavItemWrapper
              key={(child as React.ReactElement)?.props["name"]}
              data-targetid={(child as React.ReactElement)?.props["name"]}
            >
              {child}
            </NavItemWrapper>
          );
        })}
      </TopNavWrapper>
      <OverflowMenu visibilityMap={visibilityMap}>
        {orderedChildren()}
      </OverflowMenu>
    </>
  );
}

function OverflowMenu({
  children,
  visibilityMap,
}: {
  children: ReactNode;
  visibilityMap: { [key: string]: boolean };
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [menuX, setMenuX] = useState<number>();
  const [menuY, setMenuY] = useState<number>();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const open = Boolean(anchorEl);
  const theme = useContext(ThemeContext);
  const { t } = useTranslation();
  const history = useHistory();
  const { storefront_metadata } = useStoreState();
  const { user } = useAuthContext();
  const { accountPath, storePath } = useRoutePath();
  const { isMediumScreen } = useMediaQueries();
  const { route_configuration } = storefront_metadata;
  const registrationRoute = route_configuration.find(
    (route) => route.route === "registration"
  );

  const location = useLocation();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  const isProductDetailPage = (name: string, path: string) => {
    return (
      (name === t("Portfolio") ||
        path.split(":tenantSlug/")[1] === "portfolio") &&
      (location.pathname.indexOf(`${accountPath}/product/`) === 0 ||
        location.pathname.indexOf(`${storePath}/product/`) === 0)
    );
  };
  const isActive = (name: string, path: string) => {
    const tenantPath = path.replace(/:tenantSlug/, tenantSlug);
    return (
      isProductDetailPage(name, path) ||
      (location.pathname === tenantPath &&
        !isProductDetailPage(name, path) &&
        tenantPath !== storePath)
    );
  };

  const handleClick = (event: React.MouseEvent) => {
    // Stop propagation to prevent immediate close
    event.stopPropagation();

    if (anchorEl) {
      handleClose();
    } else {
      setAnchorEl(event.currentTarget);
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuX(window.innerWidth - rect.right - 5);
      setMenuY(rect.bottom + 5); // Position just below the button
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        open &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        handleClose();
      }
    };

    // Add delay to ensure portal is mounted
    const timeoutId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside, false);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("click", handleClickOutside, false);
    };
  }, [open]);

  const shouldShowMenu = useMemo(() => {
    return (
      isMediumScreen || Object.values(visibilityMap).some((v) => v === false)
    );
  }, [visibilityMap, isMediumScreen]);

  useEffect(() => {
    const handleResize = () => {
      if (open && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuX(window.innerWidth - rect.right - 5);
        setMenuY(rect.bottom + 5);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [open]);

  if (!shouldShowMenu) {
    return null;
  }
  return (
    <>
      <HamburgerWrapper ref={buttonRef} isOpen={open}>
        <InvisibleButton aria-label={t("more")} onClick={handleClick}>
          {open ? (
            <XIcon width={22} height={22} />
          ) : (
            <HamburgerIcon
              width={22}
              height={22}
              fill={
                React.Children.map(children as ReactElement, (child) => {
                  if (
                    visibilityMap[child?.props["name"]] === false &&
                    isActive(child?.props.name, child.props.path)
                  ) {
                    return true;
                  }
                  return false;
                })?.some(Boolean)
                  ? theme.brandColor
                  : undefined
              }
            />
          )}
        </InvisibleButton>
      </HamburgerWrapper>
      {open &&
        ReactDOM.createPortal(
          <DropDown ref={menuRef} menuX={menuX} menuY={menuY}>
            {React.Children.map(children as ReactElement, (child) => {
              if (visibilityMap[child?.props["name"]] === false) {
                if (child.props.childPages) {
                  const isExpanded = expandedItems.has(child.props.parentName);
                  return (
                    <div>
                      <button
                        onClick={() => {
                          setExpandedItems((prev) => {
                            const next = new Set(prev);
                            if (next.has(child.props.parentName)) {
                              next.delete(child.props.parentName);
                            } else {
                              next.add(child.props.parentName);
                            }
                            return next;
                          });
                        }}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                          textAlign: "left",
                          borderBottom: `3px solid ${
                            expandedItems.has(child.props.parentName)
                              ? theme.publicNavBorderActive
                              : "transparent"
                          }`,
                        }}
                      >
                        <span>{child.props.parentName}</span>
                        <CaretIconWrapper isExpanded={isExpanded}>
                          <CaretDownIcon width={12} height={12} />
                        </CaretIconWrapper>
                      </button>
                      {isExpanded &&
                        child.props.childPages.map((page: CustomPageConfig) => (
                          <button
                            key={page.path}
                            onClick={() => {
                              history.push(
                                `/store/${child.props.tenantSlug}${page.path}`
                              );
                              handleClose();
                            }}
                            style={{ paddingLeft: spacingTokens.spacing_3xl }}
                          >
                            {page.nav_link_text}
                          </button>
                        ))}
                    </div>
                  );
                }
                return (
                  <div onClick={handleClose}>{React.cloneElement(child)}</div>
                );
              }
              return null;
            })}
            {!user && !registrationRoute?.enabled && isMediumScreen && (
              <GuestUserWrapper>
                <LoginButton to={`${storePath}/login`}>
                  {t("Sign In")}
                </LoginButton>
              </GuestUserWrapper>
            )}
            {!user && registrationRoute?.enabled && isMediumScreen && (
              <GuestUserWrapper>
                <RegisterButton as="a" href={`${storePath}/register`}>
                  {t("Register")}
                </RegisterButton>
                <LoginButton to={`${storePath}/login`}>
                  {t("Sign In")}
                </LoginButton>
              </GuestUserWrapper>
            )}
          </DropDown>,
          document.body
        )}
    </>
  );
}

interface TopNavDropdownProps {
  parentName: string;
  childPages: CustomPageConfig[];
  tenantSlug: string;
}

export function TopNavDropdown({
  parentName,
  childPages,
  tenantSlug,
}: TopNavDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuX, setMenuX] = useState<number>();
  const [menuY, setMenuY] = useState<number>();

  const location = useLocation();

  const is_active = childPages.some(
    ({ path }) => location.pathname === `/store/${tenantSlug}${path}`
  );

  useOnClickOutside(dropdownRef, (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('a[data-dropdown-link="true"]')) {
      setIsOpen(false);
    }
  });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuX(rect.left);
      setMenuY(rect.top + DATA_DROPDOWN_LINK_TOP);
    }
  }, [isOpen]);

  return (
    <DropdownContainer ref={dropdownRef}>
      <ActiveDropdownButton
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        isExpanded={isOpen}
        className={is_active ? "active" : ""}
      >
        {parentName}
        <CaretIconWrapper isExpanded={isOpen}>
          <CaretDownIcon width={12} height={12} />
        </CaretIconWrapper>
      </ActiveDropdownButton>
      {isOpen &&
        ReactDOM.createPortal(
          <LeftDropDown menuX={menuX} menuY={menuY}>
            {childPages.map((child) => (
              <DataDropdownLink
                key={child.path}
                to={`/store/${tenantSlug}${child.path}`}
                data-dropdown-link="true"
                className={
                  location.pathname === `/store/${tenantSlug}${child.path}`
                    ? "active"
                    : ""
                }
              >
                {child.nav_link_text}
              </DataDropdownLink>
            ))}
          </LeftDropDown>,
          document.body
        )}
    </DropdownContainer>
  );
}
